/**
 * Apple HealthKit blood-glucose mirror (#65) — the single JS entry point for
 * HealthKit. Both write paths (BLE sync stores + manual entry) call
 * syncReadingsToHealthKit; nothing else may touch the native module.
 *
 * Write-only and opt-in: samples are written only when the user has enabled
 * "Sync to Apple Health" (My Account), which requests share authorization on
 * first toggle-on. Everything is a silent no-op on Android, when the feature
 * flag is off, or when the native module is absent.
 *
 * PHI: glucose values flow only into HealthKit itself (user-consented, Apple
 * secure store). They must never reach logs or analytics — events here carry
 * counts only.
 */

import {NativeModules, Platform} from 'react-native';

import {trackEvent} from '../utils/Analytics';
import {KEYS} from '../utils/Constants';
import {FEATURE_LEVEL, hasFeatureEnabled} from '../utils/FeatureConstants';
import {getData, storeData} from '../utils/GeneralStorage';

export type HealthKitWriteAuthorizationStatus = 'notDetermined' | 'denied' | 'authorized';

export type EnableHealthKitSyncResult = 'enabled' | 'denied' | 'unavailable';

export interface HealthKitGlucoseReading {
  /** Glucose value in mg/dL (BLE parsers and manual entry already normalize to mg/dL). */
  readonly glucoseValueMgdl: number;
  /** Measurement time as an ISO 8601 string (readings' dateTimeOfReading / measureAt). */
  readonly measuredAtIso: string;
  /** meter = BLE-synced; manual = keyboard entry (marks HKMetadataKeyWasUserEntered). */
  readonly source: 'meter' | 'manual';
}

/** Wire shape consumed by the native module (ios/Diathrive/HealthKitGlucoseWriter.m). */
export interface NativeGlucoseSample {
  readonly valueMgdl: number;
  readonly measuredAtIso: string;
  readonly syncIdentifier: string;
  readonly wasUserEntered: boolean;
}

interface HealthKitGlucoseWriterModule {
  isHealthDataAvailable(): Promise<boolean>;
  getWriteAuthorizationStatus(): Promise<HealthKitWriteAuthorizationStatus>;
  requestWriteAuthorization(): Promise<HealthKitWriteAuthorizationStatus>;
  saveGlucoseSamples(samples: readonly NativeGlucoseSample[]): Promise<number>;
}

const getNativeModule = (): HealthKitGlucoseWriterModule | undefined => {
  if (Platform.OS !== 'ios') {
    return undefined;
  }
  const nativeModule: HealthKitGlucoseWriterModule | undefined =
    NativeModules.HealthKitGlucoseWriter;
  return nativeModule ?? undefined;
};

/** True when the platform, feature flag, and native module all allow HealthKit sync. */
export const isHealthKitSyncSupported = (): boolean =>
  Platform.OS === 'ios' &&
  hasFeatureEnabled(FEATURE_LEVEL.healthKitSync) &&
  getNativeModule() !== undefined;

/** True when the user has opted in via the My Account toggle (default OFF). */
export const isHealthKitSyncEnabled = async (): Promise<boolean> => {
  if (!isHealthKitSyncSupported()) {
    return false;
  }
  const stored = await getData(KEYS.healthKitSync);
  return stored === 'true';
};

/**
 * Opt in: requests HealthKit share (write) authorization — this is the first
 * time the system permission sheet appears — and persists the preference only
 * when authorization is granted.
 */
export const enableHealthKitSync = async (): Promise<EnableHealthKitSyncResult> => {
  const native = getNativeModule();
  if (native === undefined || !isHealthKitSyncSupported()) {
    return 'unavailable';
  }
  try {
    const available = await native.isHealthDataAvailable();
    if (!available) {
      return 'unavailable';
    }
    const status = await native.requestWriteAuthorization();
    if (status !== 'authorized') {
      return 'denied';
    }
    await storeData(KEYS.healthKitSync, 'true');
    trackEvent('HealthKit Sync Enabled');
    return 'enabled';
  } catch {
    return 'unavailable';
  }
};

/** Opt out. Previously written samples stay in Apple Health (user-owned data). */
export const disableHealthKitSync = async (): Promise<void> => {
  await storeData(KEYS.healthKitSync, 'false');
  trackEvent('HealthKit Sync Disabled');
};

/**
 * Maps a reading to the native sample shape. The sync identifier reuses the
 * app's own reading identity (timestamp + value, the BLESyncContext dedup
 * key); combined with HKMetadataKeySyncIdentifier/-Version on the native
 * side, re-syncing the same reading upserts instead of duplicating.
 */
export const toNativeGlucoseSample = (reading: HealthKitGlucoseReading): NativeGlucoseSample => ({
  valueMgdl: reading.glucoseValueMgdl,
  measuredAtIso: reading.measuredAtIso,
  syncIdentifier: `com.diathrive.app.glucose.${reading.measuredAtIso}.${reading.glucoseValueMgdl}`,
  wasUserEntered: reading.source === 'manual',
});

const isSyncableReading = (reading: HealthKitGlucoseReading): boolean =>
  Number.isFinite(reading.glucoseValueMgdl) &&
  reading.glucoseValueMgdl > 0 &&
  typeof reading.measuredAtIso === 'string' &&
  !Number.isNaN(Date.parse(reading.measuredAtIso));

/**
 * Mirrors readings into Apple Health. Fire-and-forget by design: it never
 * throws and never blocks the calling sync/entry flow — the reading is
 * already persisted upstream, so a lost mirror write is acceptable.
 */
export const syncReadingsToHealthKit = async (
  readings: readonly HealthKitGlucoseReading[],
): Promise<void> => {
  try {
    const native = getNativeModule();
    if (native === undefined || !(await isHealthKitSyncEnabled())) {
      return;
    }
    const samples = readings.filter(isSyncableReading).map(toNativeGlucoseSample);
    if (samples.length === 0) {
      return;
    }
    const savedCount = await native.saveGlucoseSamples(samples);
    trackEvent('HealthKit Samples Written', {count: savedCount});
  } catch {
    // Counts only — never reading payloads (PHI) — and never a thrown error
    // into the caller's sync flow.
    trackEvent('HealthKit Write Failed', {count: readings.length});
  }
};
